import type { FC } from 'react';
import { Box, Typography } from '@mui/material';

import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import BarWithPill from 'src/components/followUp/common/Bar/BarWithPill';
import { nextSingleDigitNonZero } from 'src/components/followUp/common/ChartBackground/calculateBarChartValues';
import ChartBackground from 'src/components/followUp/common/ChartBackground/ChartBackground';

import styles from './index.styles';

interface GroupedBarChartProps {
  stackLabels: Array<{ name: string; color: string }>;
  bars: {
    label: string;
    stacks: Array<{ stackLabel: string; value: number }>;
  }[];
  yAxisLabel?: string;
}

const GroupedBarChartWidget: FC<GroupedBarChartProps> = ({
  bars,
  stackLabels,
  yAxisLabel,
}) => {
  const maxValue = nextSingleDigitNonZero(
    bars
      .flatMap((bar) => bar.stacks) // Combine all stacks into a single array
      .reduce((max, stack) => (stack.value > max ? stack.value : max), 9),
  );

  return (
    <Box
      sx={{
        height: '100%',
        padding: 4,
        width: '100%',
      }}
    >
      <ChartBackground
        maxValue={maxValue}
        numGridLines={4}
        labelsEachNthRow={1}
        showDecimals={0}
        yAxisLabel={yAxisLabel}
      >
        <Box sx={{ display: 'flex', height: '100%', gap: 3 }}>
          {bars.map((bar) => (
            <Box
              key={bar.label}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                }}
              >
                {stackLabels.map((label) => {
                  const stack = bar.stacks.find(
                    (stack) => stack.stackLabel === label.name,
                  );
                  if (!stack) return <Box key={label.name} />;
                  return (
                    <BarWithPill
                      key={stack.stackLabel}
                      type="vertical"
                      relativeSize={getRelativeValue(stack.value, maxValue)}
                      borderPosition="Top"
                      color={label.color}
                      borderColor={label.color}
                      pill={{
                        label: stack.value.toLocaleString(),
                        color: 'secondary',
                        squared: true,
                      }}
                      withGradient
                    />
                  );
                })}
              </Box>
              <Typography variant="body3" sx={styles.barLabel}>
                {bar.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </ChartBackground>
    </Box>
  );
};
export default GroupedBarChartWidget;
